import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { StateReferenceDTO, DependentParam, FixedParam, TextureSoil } from '../types/parameters.types';

const TEXTURES: TextureSoil[] = ['arenosa', 'media', 'argilosa', 'muitoArgilosa'];
const TEXTURE_LABELS: Record<TextureSoil, string> = {
	arenosa: 'Arenosa',
	media: 'Média',
	argilosa: 'Argilosa',
	muitoArgilosa: 'Muito Argilosa',
};
const CLASS_ORDER = ['Muito_Baixo', 'Baixo', 'Medio', 'Alto', 'Muito_Alto'] as const;
const CLASS_LABELS: Record<string, string> = {
	Muito_Baixo: 'Muito Baixo',
	Baixo: 'Baixo',
	Medio: 'Médio',
	Alto: 'Alto',
	Muito_Alto: 'Muito Alto',
};

const getTextureRanges = (param: DependentParam, texture: TextureSoil) => {
	const rule = param.rules[0];
	if (!rule) return { low: '—', medium: '—', high: '—' };
	const cls = rule.dependentClasses.find((c) => c.texture === texture);
	if (!cls) return { low: '—', medium: '—', high: '—' };
	const lowClass = cls.fixedClasses.find((c) => c.classification === 'Baixo');
	const medClass = cls.fixedClasses.find((c) => c.classification === 'Medio');
	const highClass = cls.fixedClasses.find((c) => c.classification === 'Alto');
	const low = !lowClass ? '—' : `< ${lowClass.maxValue}`;
	const medium = !medClass
		? '—'
		: medClass.minValue !== undefined
			? `${medClass.minValue} - ${medClass.maxValue}`
			: `≤ ${medClass.maxValue}`;
	const high = !highClass
		? '—'
		: highClass.minValue !== undefined
			? `> ${highClass.minValue}`
			: `> ${highClass.maxValue}`;
	return { low, medium, high };
};

const getFixedClassRange = (param: FixedParam, classification: string) => {
	const fc = param.fixedClasses.find((c) => c.classification === classification);
	if (!fc) return '—';
	if (fc.minValue !== undefined) return `${fc.minValue} - ${fc.maxValue}`;
	return `≤ ${fc.maxValue}`;
};

const buildMacroRows = (params: DependentParam[]) => {
	const textureHeaders = TEXTURES.flatMap((t) => [
		`${TEXTURE_LABELS[t]} - Baixo`,
		`${TEXTURE_LABELS[t]} - Médio`,
		`${TEXTURE_LABELS[t]} - Alto`,
	]);
	const headers = ['Parâmetro', 'Unidade', 'Método', ...textureHeaders];
	const rows = params.map((p) => {
		const rangeCols = TEXTURES.flatMap((t) => {
			const { low, medium, high } = getTextureRanges(p, t);
			return [low, medium, high];
		});
		return [p.name, p.unit, p.method, ...rangeCols];
	});
	return { headers, rows };
};

const buildFixedRows = (params: FixedParam[]) => {
	const classHeaders = CLASS_ORDER.map((c) => CLASS_LABELS[c]);
	const headers = ['Parâmetro', 'Unidade', 'Método', 'Faixa Ideal', ...classHeaders];
	const rows = params.map((p) => {
		const classCols = CLASS_ORDER.map((c) => getFixedClassRange(p, c));
		return [p.name, p.unit, p.method, p.idealRange, ...classCols];
	});
	return { headers, rows };
};

export const exportParametersToExcel = (
	regionName: string,
	stateReference: StateReferenceDTO
) => {
	const wb = XLSX.utils.book_new();

	const macroData = buildMacroRows(stateReference.dependentParams);
	const macroSheet = XLSX.utils.aoa_to_sheet([[regionName], [], macroData.headers, ...macroData.rows]);
	macroSheet['!cols'] = [{ wch: 20 }, { wch: 10 }, { wch: 18 }, ...Array(12).fill({ wch: 14 })];
	XLSX.utils.book_append_sheet(wb, macroSheet, 'Macronutrientes');

	const microCodes = ['B', 'Cu', 'Fe', 'Mn', 'Zn'];
	const microParams = stateReference.fixedParams.filter((p) => microCodes.includes(p.code));
	const microData = buildFixedRows(microParams);
	const microSheet = XLSX.utils.aoa_to_sheet([[regionName], [], microData.headers, ...microData.rows]);
	microSheet['!cols'] = [{ wch: 20 }, { wch: 10 }, { wch: 18 }, { wch: 14 }, ...Array(5).fill({ wch: 14 })];
	XLSX.utils.book_append_sheet(wb, microSheet, 'Micronutrientes');

	const chemParams = stateReference.fixedParams.filter((p) => !microCodes.includes(p.code));
	const chemData = buildFixedRows(chemParams);
	const chemSheet = XLSX.utils.aoa_to_sheet([[regionName], [], chemData.headers, ...chemData.rows]);
	chemSheet['!cols'] = [{ wch: 20 }, { wch: 10 }, { wch: 18 }, { wch: 14 }, ...Array(5).fill({ wch: 14 })];
	XLSX.utils.book_append_sheet(wb, chemSheet, 'Índices Químicos');

	XLSX.writeFile(wb, `parametros-${regionName}.xlsx`);
};

export const exportParametersToPdf = (
	regionName: string,
	stateReference: StateReferenceDTO
) => {
	const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

	doc.setFontSize(16);
	doc.text(`Parâmetros - ${regionName}`, 14, 15);

	doc.setFontSize(12);
	doc.text('Macronutrientes', 14, 28);

	const macroData = buildMacroRows(stateReference.dependentParams);
	autoTable(doc, {
		startY: 32,
		head: [macroData.headers],
		body: macroData.rows,
		styles: { fontSize: 6, cellPadding: 1.5 },
		headStyles: { fillColor: [69, 58, 241], textColor: 255, fontStyle: 'bold' },
		alternateRowStyles: { fillColor: [245, 245, 245] },
	});

	const microCodes = ['B', 'Cu', 'Fe', 'Mn', 'Zn'];
	const microParams = stateReference.fixedParams.filter((p) => microCodes.includes(p.code));
	const microData = buildFixedRows(microParams);
	const afterMacro = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
	doc.setFontSize(12);
	doc.text('Micronutrientes', 14, afterMacro);
	autoTable(doc, {
		startY: afterMacro + 4,
		head: [microData.headers],
		body: microData.rows,
		styles: { fontSize: 7, cellPadding: 1.5 },
		headStyles: { fillColor: [69, 58, 241], textColor: 255, fontStyle: 'bold' },
		alternateRowStyles: { fillColor: [245, 245, 245] },
	});

	const chemParams = stateReference.fixedParams.filter((p) => !microCodes.includes(p.code));
	const chemData = buildFixedRows(chemParams);
	doc.addPage();
	doc.setFontSize(12);
	doc.text('Índices Químicos', 14, 20);
	autoTable(doc, {
		startY: 24,
		head: [chemData.headers],
		body: chemData.rows,
		styles: { fontSize: 7, cellPadding: 1.5 },
		headStyles: { fillColor: [69, 58, 241], textColor: 255, fontStyle: 'bold' },
		alternateRowStyles: { fillColor: [245, 245, 245] },
	});

	doc.save(`parametros-${regionName}.pdf`);
};
